import * as React from "react";
import * as queryString from "query-string";

import { useProminentError } from "../../../Common";

export enum ContentRoutes {
  Purchase = "purchase",
  CheckBalance = "check-balance",
}

export type ContentRoute = `${ContentRoutes}`;

interface ContentRouteConsumerInterface {
  hasInitialized: boolean;
  selectedContentRoute: ContentRoute;

  onUserWantsToSelectContentRoute: (route: ContentRoute) => void;
}

const defaultValue = {
  hasInitialized: false,
  selectedContentRoute: ContentRoutes.Purchase,
};

const ContentRouteContext = React.createContext(
  defaultValue as ContentRouteConsumerInterface
);

interface ContentRouteProviderProps {
  children?: React.ReactNode;
}

const QUERY_PARSING_STRINGIFY_COMMON_ARGUMENTS: queryString.ParseOptions = {
  arrayFormat: "comma",
  parseBooleans: true,
};

export const ContentRouteProvider: React.FunctionComponent<
  ContentRouteProviderProps
> = (props) => {
  const { setProminentError } = useProminentError();

  const [hasInitialized, setHasInitialized] = React.useState(false);

  const [selectedContentRoute, setSelectedContentRoute] =
    React.useState<ContentRoute>(ContentRoutes.Purchase);

  React.useEffect(() => {
    synchronizeWithUrlQueryParameters();
    setHasInitialized(true);
  }, []);

  const synchronizeWithUrlQueryParameters = () => {
    const currentLocationInFull = "" + window.location;

    const currentUrlData = queryString.parseUrl(currentLocationInFull, {
      ...QUERY_PARSING_STRINGIFY_COMMON_ARGUMENTS,
      ...{
        parseFragmentIdentifier: true,
      },
    });

    let my_route: ContentRoute;
    const route_fragment_identified_unverified =
      currentUrlData.fragmentIdentifier || "";
    const verifiedRoute = Object.values(ContentRoutes).find(
      (a_content_route) =>
        a_content_route === route_fragment_identified_unverified
    );
    if (verifiedRoute) {
      my_route = verifiedRoute;
    } else {
      my_route = ContentRoutes.Purchase;
    }

    if (my_route != selectedContentRoute) {
      setSelectedContentRoute(my_route);
    }

    /**
     * Extension into query routing suggestion
     */
    // const queryEntries = Object.entries(currentUrlData.query);

    // for (const [key, value] of queryEntries) {
    //   if (!value) {
    //     continue;
    //   }
    //   if (key === "route") {
    //     const route = value as ContentRoute;
    //   }
    // }
  };

  const updateQueryInBrowserUrlBar = (queryValuesDescription: {
    [key: string]: string | string[];
  }): void => {
    const routeToCommit = queryValuesDescription.route;

    /**
     * Extension for query routing suggestion
     */
    // const newQueryValue = queryString.stringify(
    //   {
    //     ...queryValuesDescription,
    //   },
    //   {
    //     ...QUERY_PARSING_STRINGIFY_COMMON_ARGUMENTS,
    //   }
    // );

    const newQuery = routeToCommit ? `#${routeToCommit}` : "";

    /**
     * TODO: refactor this to use a more robust solution / builder pattern.
     */
    const newUrl = `${window.location.protocol}//${window.location.host}${window.location.pathname}${newQuery}`;

    window.history.replaceState({ path: newUrl }, "", newUrl);
  };

  const onUserWantsToSelectContentRoute = (route: ContentRoute) => {
    setProminentError(null);
    setSelectedContentRoute(route);
    updateQueryInBrowserUrlBar({
      route,
    });
  };

  return (
    <ContentRouteContext.Provider
      value={{
        hasInitialized,
        selectedContentRoute,

        onUserWantsToSelectContentRoute,
      }}
    >
      {props?.children}
    </ContentRouteContext.Provider>
  );
};

export const useContentRoute = (): ContentRouteConsumerInterface =>
  React.useContext(ContentRouteContext);
